import React from 'react';
import { FiWifi, FiWifiOff, FiRefreshCw, FiAlertTriangle } from 'react-icons/fi';

const NetworkError = ({ 
  error, 
  onRetry, 
  isRetrying = false, 
  title = "Connection Issue",
  showRetryButton = true,
  errorType = 'network'
}) => {
  // Determine if this is a network error vs server error
  const isNetworkError = errorType === 'network' || 
    !error || 
    error.includes('Network Error') || 
    error.includes('fetch') || 
    error.includes('timeout') ||
    error.includes('ERR_NETWORK') ||
    error.includes('ERR_INTERNET_DISCONNECTED') ||
    error.includes('Failed to fetch');

  const isAuthError = errorType === 'auth' || 
    error.includes('Authentication') || 
    error.includes('Access denied') ||
    error.includes('insufficient permissions');

  const getErrorIcon = () => {
    if (isAuthError) {
      return <FiAlertTriangle className="w-12 h-12 text-yellow-500" />;
    }
    if (isNetworkError) {
      return <FiWifiOff className="w-12 h-12 text-red-500" />;
    }
    return <FiAlertTriangle className="w-12 h-12 text-orange-500" />;
  };

  const getErrorTitle = () => {
    if (isAuthError) {
      return "Access Issue";
    }
    if (isNetworkError) {
      return "Connection Problem";
    }
    return "Something went wrong";
  };

  const getErrorMessage = () => {
    if (isAuthError) {
      return "There's an issue with your access permissions. Please contact your administrator or try logging in again.";
    }
    if (isNetworkError) {
      return "We're having trouble connecting to our servers. Please check your internet connection and try again.";
    }
    return "There was an issue loading your dashboard. Our team has been notified and we're working to fix it.";
  };

  const getSuggestions = () => {
    if (isAuthError) {
      return [
        "Try logging out and logging back in",
        "Contact your system administrator",
        "Check if your account has the required permissions"
      ];
    }
    if (isNetworkError) {
      return [
        "Check your internet connection",
        "Try refreshing the page",
        "Contact IT support if the problem persists"
      ];
    }
    return [
      "Try refreshing the page",
      "Clear your browser cache",
      "Contact support if the problem continues"
    ];
  };

  return (
    <div className="p-4 py-6 md:p-8 space-y-6 md:space-y-8">
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md mx-auto">
          {/* Error Icon */}
          <div className="flex justify-center mb-6">
            {getErrorIcon()}
          </div>

          {/* Error Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {getErrorTitle()}
          </h2>

          {/* Error Message */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {getErrorMessage()}
          </p>

          
          {/* Retry Button */}
          {showRetryButton && (
            <button
              onClick={onRetry}
              disabled={isRetrying}
              className={`
                inline-flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200
                ${isRetrying 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
                }
              `}
            >
              {isRetrying ? (
                <>
                  <FiRefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <FiRefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </>
              )}
            </button>
          )}

          {/* Technical Details (for debugging) */}
          {process.env.NODE_ENV === 'development' && error && (
            <details className="mt-6 text-left">
              <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                Technical Details
              </summary>
              <div className="mt-2 p-3 bg-gray-100 rounded text-xs text-gray-600 font-mono">
                {error}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetworkError;
